import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';

const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-frost-vanilla">
      {/* Dynamic Background Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-frost-ice rounded-full blur-[100px] opacity-60"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-frost-berry/5 rounded-full blur-[120px] opacity-40"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-20 lg:py-0">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-3 py-2 px-4 rounded-full bg-white border border-frost-ice shadow-sm mb-8">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-frost-berry opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-frost-berry"></span>
              </span>
              <span className="text-xs font-black uppercase tracking-widest text-frost-berry">
                Next Batch Shipping Monday
              </span>
            </div>
            
            <h1 className="text-6xl lg:text-[5.5rem] font-bold mb-8 leading-[0.95] tracking-tighter">
              Indulgence, <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-frost-berry via-pink-600 to-frost-berry bg-[length:200%_auto] animate-gradient">
                Deep-Frozen.
              </span>
            </h1>
            
            <p className="text-xl text-gray-500 mb-12 max-w-lg leading-relaxed font-medium">
              Experience artisanal dessert flights delivered via our signature cold-chain logistics. Perfectly frozen, every single time.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <Link to="/shop" className="btn-primary flex items-center justify-center gap-3 group px-10">
                Explore The Flight List
                <SafeIcon icon="FiArrowRight" className="group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link to="/info" className="btn-secondary px-10 flex items-center justify-center">
                Our Cold-Chain Policy
              </Link>
            </div>

            <div className="mt-16 flex items-center gap-8 pt-8 border-t border-frost-ice/50">
              <div>
                <p className="text-3xl font-serif font-black text-frost-berry">4.9/5</p>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Customer Rating</p>
              </div>
              <div className="w-px h-10 bg-frost-ice"></div>
              <div>
                <p className="text-3xl font-serif font-black text-frost-berry">100%</p>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Frozen Guarantee</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="relative"
          >
            {/* Main Image Container */}
            <div className="relative z-10 p-4 bg-white rounded-[3.5rem] shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-700">
              <img 
                src="https://images.unsplash.com/photo-1563805042-7684c8a9e9cb?auto=format&fit=crop&q=80&w=1200" 
                alt="Premium Ice Cream" 
                className="w-full h-[600px] object-cover rounded-[2.8rem]"
              />
            </div>

            {/* Floating Elements */}
            <motion.div 
              animate={{ y: [0, -20, 0] }}
              transition={{ repeat: Infinity, duration: 5 }}
              className="absolute -top-10 -right-10 z-20 bg-white p-6 rounded-3xl shadow-2xl border border-frost-ice"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-frost-ice rounded-2xl flex items-center justify-center">
                  <SafeIcon icon="FiThermometer" className="text-2xl text-frost-berry" />
                </div>
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-tighter">-109°F</p>
                  <p className="font-bold text-frost-berry">Dry Ice Tech</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0, 20, 0] }}
              transition={{ repeat: Infinity, duration: 6, delay: 0.5 }}
              className="absolute -bottom-10 -left-10 z-20 bg-frost-berry text-white p-8 rounded-[2.5rem] shadow-2xl"
            >
              <div className="flex flex-col gap-1">
                <div className="flex gap-1 mb-2">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} icon="FiStar" className="text-xs fill-current text-white" />)}
                </div>
                <p className="text-lg font-serif font-italic italic">"Arrived perfectly frozen in Miami!"</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70">— Jessica K.</p>
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;